import React, { FunctionComponent, useCallback, useState, useMemo, useRef, ChangeEventHandler, InputHTMLAttributes, ComponentType } from 'react';
import ReactSelect from 'react-select';
import InputMask from 'react-input-mask';
import { tryInvoke } from '@factorin/util';

import { ClassName, cn, animation, multipleRef } from '../../utils';
import { useInputOnChange } from '../../hooks';
import { TransitionComponent } from '../TransitionComponent';
import { Button } from '../Button';
import { Link } from '../Typography';
import { ClosingOverlay } from '../ClosingOverlay';
import { ProgressLoader } from '../ProgressLoader';
import { Icon } from '../Icon';
import info from '../../icons/info.sprite.svg';
import doneIcon from '../../icons/done.sprite.svg';
import search from '../../icons/help.sprite.svg';
import drop from '../../icons/drop.sprite.svg';
import styles from './index.module.scss';


export interface FormControlBase<TValue> {
    error?: React.ReactNode;
    active?: boolean;
    needInitialValidate?: boolean;

    name?: string;
    value?: TValue;
    onChange?: (value: TValue) => void;
    onBlur?: (event: React.FocusEvent<HTMLElement | any>) => void;
    onFocus?: (event: React.FocusEvent<HTMLElement | any>) => void;

    inputProps?: { [p: string]: any };
}

interface FormControl<TValue> extends FormControlBase<TValue> {
    size?: '48' | '56';
    label?: string;
    disabled?: boolean;
}

type VisibleValueProps = ClassName & {
    onClick: () => void;
}

export interface InputProps extends FormControl<string>, ClassName {
    type?: 'text' | 'password';
    placeholder?: string;
    mask?: string;
    needEvent?: boolean;
    autoComplete?: HTMLInputElement['autocomplete'];
    visibleValue?: ComponentType<VisibleValueProps>;
    readOnly?: HTMLInputElement['readOnly'];
}

const ControlIcon = ({ icon, className = '' }) => (
    <Icon className={cn(styles.icon, 'vcenter', className)} size='24' icon={icon} />
);

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
    className,
    size = '48', label, error, disabled,
    name, value, onChange, onBlur, onFocus,
    type = 'text', placeholder, mask,
    needEvent, visibleValue: VisibleValue,
    autoComplete, readOnly,
}, ref) => {
    const inputRef = useRef<HTMLInputElement>();
    const Component = useMemo(
        () => mask
            ? React.forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>((
                props: InputHTMLAttributes<HTMLInputElement>,
                ref,
            ) => (
                <InputMask inputRef={ref} mask={mask} maskChar={null} {...props} />
            ))
            : 'input',
        [mask],
    );

    return (
        <div className={cn(styles.container, className)}>
            <Component
                ref={multipleRef(ref, inputRef)}
                className={cn(
                    styles.control,
                    styles.input,
                    styles[`size-${size}`],
                    error && styles.error,
                    error && styles.hasIcon,
                    readOnly && styles.readOnly,
                )}
                name={name}
                type={type}
                value={value}
                disabled={disabled}
                onChange={useInputOnChange(onChange, needEvent)}
                onBlur={onBlur}
                onFocus={onFocus}
                placeholder={placeholder}
                autoComplete={autoComplete}
                readOnly={readOnly}
            />
            {label && (
                <span className={cn(
                    styles.label,
                    (value || VisibleValue) && styles.labelUp,
                    error && styles.error,
                )}
                >
                    {label}
                </span>
            )}
            {VisibleValue && (
                <VisibleValue
                    className={styles.visibleOption}
                    onClick={() => inputRef.current && inputRef.current.focus()}
                />
            )}
            {error && (
                <span className={styles.errorLabel}>
                    {error}
                </span>
            )}
            <span className={styles.shadow} />
            {error && (
                <ControlIcon className={styles.errorIcon} icon={info} />
            )}
        </div>
    );
});

export interface SelectProps extends FormControl<any>, ClassName {
    options: { label: string, value: any }[];

    direction?: 'up' | 'down';
    searchable?: boolean;
    searchPlaceholder?: string;
    noOptionsPlaceholder?: string;
}

const noFilter = () => true;

const SearchIcon = () => <Icon size='24' icon={search} />;

const SelectOption = ({ label, value, innerProps, innerRef, isFocused, selectProps }) => (
    <div
        className={cn(
            styles.selectOption,
            isFocused && styles.focused,
            value === selectProps.value && styles.selected,
        )}
        {...innerProps}
        ref={innerRef}
    >
        {label}
    </div>
);

export const Select: FunctionComponent<SelectProps> = ({
    className,
    size, label, error, disabled,
    name, value, onChange, onBlur, onFocus,
    direction, options, searchable, searchPlaceholder, noOptionsPlaceholder,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleOpen = useCallback(value => setIsOpen(value), []);
    const onSelect = useCallback(
        ({ value }) => {
            onChange(value);
            toggleOpen(false);
        },
        [onChange, toggleOpen],
    );
    const onSelectBlur = useCallback(
        e => {
            tryInvoke(onBlur, e);
            toggleOpen(false);
        },
        [onBlur, toggleOpen],
    );
    const onMenuOpen = useCallback(
        e => {
            tryInvoke(onFocus, e);
            toggleOpen(true);
        },
        [onFocus, toggleOpen],
    );
    const selectStyles = useMemo(
        () => ({
            control: (provided: object) => ({
                ...provided,
                border: 'none',
                ...!searchable && {
                    position: 'absolute' as const,
                    opacity: 0,
                    height: 0,
                },
            }),
            menu: () => ({ boxShadow: 'none' }),
            menuList: () => ({
                padding: 0,
                overflowY: 'auto' as const,
                maxHeight: 280,
            }),
        }),
        [searchable],
    );
    const selectedOption = useMemo(
        () => options.find(({ value: optionValue }) => optionValue === value),
        [options, value],
    );
    return (
        <div className={cn(styles.container, className)}>
            <Button
                variant='auxiliary'
                className={cn(
                    styles.control,
                    styles.selectButton,
                    styles[`size-${size}`],
                    styles.hasIcon,
                    error && styles.error,
                    isOpen && styles.isOpen,
                )}
                disabled={isOpen || disabled}
                onClick={onMenuOpen}
            >
                <span>
                    {(selectedOption || {} as { label?: string }).label}
                </span>
            </Button>
            {label && (
                <span className={cn(styles.label, value && styles.labelUp, error && styles.error)}>
                    {label}
                </span>
            )}
            {error && (
                <span className={styles.errorLabel}>
                    {error}
                </span>
            )}
            <span className={styles.shadow} />
            {isOpen && <ClosingOverlay />}
            <ControlIcon icon={drop} className={error && styles.errorIcon} />
            <TransitionComponent timeout={animation / 2} in={isOpen}>
                <div className={cn(styles.selectMenu, styles[direction])}>
                    <ReactSelect
                        className={styles.reactSelect}
                        options={options}
                        value={value}
                        onChange={onSelect}
                        isDisabled={disabled}
                        name={name}

                        autoFocus
                        styles={selectStyles}
                        components={{
                            IndicatorSeparator: null,
                            DropdownIndicator: SearchIcon,
                            Option: SelectOption as any,
                        }}
                        backspaceRemovesValue={false}
                        isSearchable={false}
                        filterOption={noFilter}
                        placeholder={searchPlaceholder}
                        menuIsOpen
                        onBlur={onSelectBlur}
                        noOptionsMessage={() => noOptionsPlaceholder}
                    />
                </div>
            </TransitionComponent>
        </div>
    );
};

Select.defaultProps = {
    size: '48',
    noOptionsPlaceholder: '-',
    direction: 'down',
};

interface FileControlProps extends FormControl<File | string>, ClassName {
    progress?: number;
    indeterminate?: boolean;
    done?: boolean;
    onFileClick?: () => void;
}

export const FileControl: FunctionComponent<FileControlProps> = ({
    className, children,
    size, label, error, disabled,
    name, value, onChange, progress, indeterminate, done, onFileClick,
}) => {
    const input = useRef<HTMLInputElement>();
    const onPick = useCallback<ChangeEventHandler<HTMLInputElement>>(
        ({ target }) => onChange(target.files[0]),
        [onChange],
    );
    const open = useCallback(
        () => {
            input.current.value = '';
            input.current.click();
        },
        [],
    );
    const NameComponent = value && onFileClick ? Link : 'div';
    return (
        <div className={cn(styles.fileControl, className)}>
            <input
                ref={input}
                name={name}
                className={styles.input}
                type='file'
                tabIndex={-1}
                onChange={onPick}
            />
            <div className={cn(
                styles.control,
                styles[`size-${size}`],
                styles.fileName,
                error && styles.error,
            )}
            >
                <NameComponent
                    className={styles.fileNameContent}
                    onClick={value ? onFileClick : undefined}
                >
                    {value instanceof File ? value.name : value}
                </NameComponent>
                <div className={cn('vcenter', styles.icon)}>
                    {(progress || indeterminate) && !done && (
                        <ProgressLoader value={progress} indeterminate={indeterminate} />
                    )}
                    {done && (
                        <Icon icon={doneIcon} size='24' />
                    )}
                </div>
            </div>
            {label && (
                <span className={cn(styles.label, value && styles.labelUp, error && styles.error)}>
                    {label}
                </span>
            )}
            {error && (
                <span className={styles.errorLabel}>
                    {error}
                </span>
            )}
            <Button
                variant='background'
                size={size}
                className={cn(styles.fileButton, error && styles.error)}
                onClick={open}
                disabled={disabled}
            >
                {children}
            </Button>
        </div>
    );
};

FileControl.defaultProps = {
    size: '48',
};
